import React from 'react';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import { type FilterOptionsState } from '@mui/material';
import Autocomplete, {
  createFilterOptions,
  type AutocompleteChangeReason,
  type AutocompleteProps,
} from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import classNames from 'classnames';
import cuid from 'cuid';
import useLabels from '@/lib/hooks/swr/useLabels';
import translate from '@/lib/intl/translate';
import type LabelSelectValue from '@/types/LabelSelectValue';

const filter = createFilterOptions<LabelSelectValue>();

export type LabelSelectProps = {
  className?: string;
  error?: boolean;
  freeSolo?: boolean;
  helperText?: string;
  id?: string;
  label?: string;
  name?: string;
  onChange?(
    event: Omit<React.SyntheticEvent<Element, Event>, 'target'> & {
      target: { name: string; value: LabelSelectValue[] };
    },
  ): void;
  placeholder?: string;
  required?: boolean;
  value?: LabelSelectValue[];
} & Omit<
  AutocompleteProps<LabelSelectValue, boolean, undefined, boolean, 'input'>,
  'options' | 'renderInput' | 'renderTags'
>;

function LabelSelect({
  className = '',
  error = false,
  freeSolo = false,
  id = '',
  helperText = '',
  label = translate('CONSTITUENT_LABELS'),
  name = 'labels',
  onChange,
  placeholder = translate('CONSTITUENT_LABELS'),
  required = false,
  value = [],
  ...rest
}: LabelSelectProps) {
  const [uid] = React.useState(id || cuid());
  const [searchValue, setSearchValue] = React.useState('');
  const searchParams = React.useMemo(
    () => (searchValue ? { labelName: searchValue } : {}),
    [searchValue],
  );
  const { data: labels } = useLabels(searchParams);

  const handleChange = React.useCallback(
    (
      event: React.SyntheticEvent<Element, Event>,
      val: LabelSelectValue | string | Array<LabelSelectValue | string> | null,
      reason: AutocompleteChangeReason,
    ) => {
      if (
        val !== null &&
        (typeof val === 'string' ||
          (typeof val === 'object' && !Array.isArray(val)))
      ) {
        // eslint-disable-next-line no-param-reassign
        val = [val];
      }

      let isCreating = reason === 'createOption';
      const newValue = (val || [])
        // TODO(jon): This filters out `undefined` values in the value array. I'm
        // not sure why this happens, but we've seen some Sentry errors where the
        // UI is trying to read `inputValue` of undefined.
        .filter(Boolean)
        .map((item): LabelSelectValue => {
          // The `val` is an array of objects with labelName and labelId. A newly
          // created item, however, will eiher be a string if the `createOption`
          // reason was given, or an object with an `inputValue` if the user
          // creates the new option by clicking "Add X" in the dropdown. So we
          // need to normalize these values.
          if (typeof item === 'object' && item.inputValue) {
            isCreating = true;
            return item.inputValue;
          }
          if (typeof item === 'string') {
            return {
              labelName: item.trim(),
              labelId: null,
            } as LabelSelectValue;
          }
          return item;
        })
        .filter(({ labelName }: LabelSelectValue) => labelName); // Remove any items that are just trimmed whitespace

      if (isCreating) {
        // TODO(jon): Previously we would do some validation here to ensure the
        // user was not creating a duplicate label. This becomes harder with the
        // labels coming from a search endpoint rather than an array of values, so
        // we're going to punt on this for now.
      }

      if (onChange) {
        onChange({ ...event, target: { name, value: newValue } });
      }
    },
    [onChange],
  );

  return (
    <Autocomplete
      multiple
      className={classNames(className, 'mb-0 w-full')}
      id={uid}
      isOptionEqualToValue={(option, val) => option?.labelId === val.labelId}
      options={labels || []}
      getOptionLabel={(option) => (option as LabelSelectValue).labelName}
      inputValue={searchValue}
      value={value}
      onChange={handleChange}
      freeSolo={freeSolo}
      onClose={() => setSearchValue('')}
      onInputChange={(_, val) => setSearchValue(val)}
      filterOptions={(
        options: LabelSelectValue[],
        params: FilterOptionsState<LabelSelectValue>,
      ): LabelSelectValue[] => {
        const filtered = filter(options, params);
        const trimmedValue = params.inputValue.trim();
        if (
          freeSolo &&
          trimmedValue &&
          !filtered
            .map(({ labelName }: LabelSelectValue) => labelName.toLowerCase())
            .includes(trimmedValue.toLowerCase())
        ) {
          filtered.push({
            inputValue: { labelName: trimmedValue, labelId: null },
            labelName: translate('ADD_ITEM', { item: trimmedValue }),
          });
        }
        return filtered;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          helperText={helperText}
          label={label}
          name={name}
          placeholder={placeholder}
          required={required && !value.length}
        />
      )}
      renderOption={(props, option) => (
        <li className="flex w-full items-center" {...props}>
          {!!option.labelId && (
            <Checkbox
              className="py-2 pl-0"
              checked={value
                .map(({ labelId }) => labelId)
                .includes(option.labelId)}
            />
          )}
          <div>{(option.labelName || option) as string}</div>
        </li>
      )}
      renderTags={(val, getTagProps) =>
        val.map((option, index) => (
          <Chip
            label={option.labelName}
            size="small"
            {...getTagProps({ index })}
          />
        ))
      }
      selectOnFocus={freeSolo}
      {...rest}
    />
  );
}

export default LabelSelect;
